// import type User from '../../backend/app/models/user' // Assuming User might be needed for created_by or similar
// import type Organisation from '../../backend/app/models/organisation' // Assuming Organisation might be relevant
import { defineStore } from "pinia";
import type { Grade } from "~/types/global";
import type Observation from "../../../backend/app/models/observation"; // Adjusted import for Observation model

interface ObservationResponse {
  data: Partial<Observation>[];
  meta: {
    total: number;
    lastPage: number;
    currentPage: number;
  };
}

type DashboardMetric = {
  category: { id: string; label: string; icon: string };
  obsWeightedAvg: number;
  obsCount: number;
  safeguardingCount: number;
  weeksSinceLastObs: number | null;
  dwSessionCount: number;
  dwWeightedAvg: number | null;
  weeksSinceLastDw: number | null;
  focusScore: number;
};

export type ResidentObsStats = {
  strongest: DashboardMetric | null;
  mostNeeded: DashboardMetric | null;
  suggestedNext: DashboardMetric | null;
  needsData: DashboardMetric | null;
  atRisk: DashboardMetric | null;
  untouched: { category: { id: string; label: string; icon: string } } | null;
  focusAreas: DashboardMetric[];
  gapsInData: DashboardMetric[];
  hasData: boolean;
};

export const useObservationStore = defineStore("observation", () => {
  const auth = useAuthStore();
  const toast = useToast();
  // const familyGroupsStore = useGroupStore() // Assuming this is not directly needed for observations store
  const observations = ref<Partial<Observation>[]>([]); // Using Observation type
  const needRefresh = ref(false);

  const createObservation = async (observationData: any) => {
    try {
      const resp = await auth.api<{
        observation: Observation;
        message: string;
      }>("post", "/observation/create", observationData);

      needRefresh.value = true;

      return resp;
    } catch (error: any) {
      console.error("Error creating observation:", error);
      const description =
        error.data?.message || error.message || "Could not create observation. Please try again.";
      toast.add({ title: "Error", description, color: "error" }); // Using 'error' for errors
      throw new Error(description);
    }
  };

  const getObservations = async (query?: Record<string, any>): Promise<ObservationResponse> => {
    try {
      const resp = await auth.api<ObservationResponse>("get", "/observation/index", {
        queryString: query,
      });
      observations.value = resp.data;
      return resp;
    } catch (error: any) {
      console.error("Error fetching observations:", error);
      const description = error.data?.message || error.message || "Could not fetch observations.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const showObservation = async (id?: number): Promise<{ data: Observation }> => {
    if (!id || isNaN(id)) {
      const error = new Error("Invalid observation ID");
      toast.add({ title: "Error", description: "Invalid observation ID", color: "error" });
      throw error;
    }

    try {
      const resp = await auth.api<Observation>("get", `/observation/show/${id}`);
      // Backend returns a single observation, wrap it in data property for consistency
      return { data: resp as Observation };
    } catch (error: any) {
      console.error("Error fetching observation:", error);
      const description = error.data?.message || error.message || "Could not fetch observation.";
      toast.add({ title: "Error", description, color: "error" });
      throw error;
    }
  };

  const getUserObservationsCount = async (residentId: string) => {
    try {
      const resp = await auth.api<{ count: number }>("GET", `/observation/count/${residentId}`);

      return resp.count;
    } catch (error: any) {
      console.error("Error fetching observations count:", error);
      const description =
        error.data?.message || error.message || "Could not fetch observations count.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getResidentObsGrades = async (residentId: string) => {
    try {
      const resp = await auth.api<Grade[]>("GET", `/observation/grades/${residentId}`);

      return resp;
    } catch (error: any) {
      console.error("Error fetching observations grades:", error);
      const description =
        error.data?.message || error.message || "Could not fetch observations grades.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getResidentObsStats = async (residentId: string) => {
    try {
      const resp = await auth.api<ResidentObsStats>("GET", `/observation/stats/${residentId}`);
      return resp;
    } catch (error: any) {
      console.error("Error fetching observation stats:", error);
      const description =
        error.data?.message || error.message || "Could not fetch observation stats.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const updateObservation = async (observationId: number, updateData: any) => {
    try {
      const resp = await auth.api<{
        observation: Observation;
        message: string;
      }>("put", `/observation/update/`, { ...updateData, id: observationId });

      toast.add({
        icon: "i-heroicons-eye",
        title: "Success",
        description: resp.message || "Observation updated.",
        color: "success",
        duration: 2000,
      });
      needRefresh.value = true;

      return true;
    } catch (error) {
      console.error("Error updating observation:", error);

      toast.add({
        title: "Error",
        description: "Could not update observation.",
        color: "error",
      });

      throw new Error(
        (error as { data?: { message?: string }; message?: string })?.data?.message ||
          (error as { message?: string })?.message ||
          "Could not update observation.",
      );
    }
  };

  const deleteObservation = async (observationId: string) => {
    try {
      const resp = await auth.api<{ message: string }>("delete", `/observation/delete/`, {
        observationId,
      });

      needRefresh.value = true;
      // toast.add({ title: 'Success', description: resp.message || 'Observation deleted.', color: 'green' })
      return true;
    } catch (error: any) {
      console.error("Error deleting observation:", error);
      const description = error.data?.message || error.message || "Could not delete observation.";
      toast.add({ title: "Error", description, color: "error" });
      return false;
    }
  };

  return {
    observations,
    createObservation,
    getObservations,
    showObservation,
    updateObservation,
    deleteObservation,
    needRefresh,
    getUserObservationsCount,
    getResidentObsGrades,
    getResidentObsStats,
  };
});
